import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS, Step, TooltipRenderProps, type EventData } from 'react-joyride';
import { IS_ADMIN } from '../../utils/role';
import { useUpdateInvoiceTutorialVersionMutation } from '../../store/user/user.api';
import {
   ACCOUNTITZ_TUTORIAL_RESTART_EVENT,
   consumeTutorialRestartRequest,
   INVOICE_TUTORIAL_VERSION,
} from './tutorial-storage';
import './accountitz-tutorial.scss';

interface PropsType {
   completedVersion?: number;
   userKey?: string;
}

const MOBILE_BREAKPOINT = 768;

const TutorialTooltip = ({
   index,
   isLastStep,
   primaryProps,
   size,
   step,
   tooltipProps,
}: TooltipRenderProps) => {
   const [translate] = useTranslation('common');

   return (
      <div {...tooltipProps} className='accountitz-tour-tooltip'>
         <div className='accountitz-tour-copy'>
            {step.title && <h2>{step.title}</h2>}
            <div className='accountitz-tour-description'>{step.content}</div>
         </div>
         <div className='accountitz-tour-footer'>
            <button {...primaryProps} type='button' className='accountitz-tour-next'>
               {isLastStep ? translate('tutorial.done') : translate('tutorial.next')}
            </button>
            <div
               className='accountitz-tour-progress'
               aria-label={String(
                  translate('tutorial.stepProgress', { current: index + 1, total: size }),
               )}
            >
               {Array.from({ length: size }).map((_, dotIndex) => (
                  <span
                     className={dotIndex === index ? 'is-active' : ''}
                     aria-hidden='true'
                     key={dotIndex}
                  />
               ))}
            </div>
         </div>
      </div>
   );
};

const AccountitzTutorial = ({ completedVersion, userKey }: PropsType) => {
   const [translate] = useTranslation('common');
   const [showWelcome, setShowWelcome] = useState(false);
   const [startAfterWelcome, setStartAfterWelcome] = useState(false);
   const [runTour, setRunTour] = useState(false);
   const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
   const checkedUser = useRef<string | undefined>(undefined);
   const [updateInvoiceTutorialVersion] = useUpdateInvoiceTutorialVersionMutation();

   const isAdmin = IS_ADMIN();

   useEffect(() => {
      const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`);
      const updateViewport = () => setIsMobile(mediaQuery.matches);

      updateViewport();
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
   }, []);

   useEffect(() => {
      if (!userKey || checkedUser.current === userKey) return;

      checkedUser.current = userKey;
      const restartRequested = consumeTutorialRestartRequest();
      const completedOnServer = (completedVersion ?? 0) >= INVOICE_TUTORIAL_VERSION;

      if (restartRequested || !completedOnServer) {
         setShowWelcome(true);
      }
   }, [completedVersion, updateInvoiceTutorialVersion, userKey]);

   useEffect(() => {
      const handleRestart = () => {
         consumeTutorialRestartRequest();
         setRunTour(false);
         setStartAfterWelcome(false);
         setShowWelcome(true);
      };

      window.addEventListener(ACCOUNTITZ_TUTORIAL_RESTART_EVENT, handleRestart);
      return () => window.removeEventListener(ACCOUNTITZ_TUTORIAL_RESTART_EVENT, handleRestart);
   }, []);

   const steps = useMemo<Step[]>(() => {
      const tutorialSteps: Step[] = [
         {
            id: 'navigation',
            target: isMobile
               ? '[data-tour="mobile-navigation-anchor"]'
               : '[data-tour="desktop-navigation-anchor"]',
            spotlightTarget: '[data-tour="main-navigation"]',
            placement: 'bottom',
            title: translate('tutorial.navigation.title'),
            content: translate('tutorial.navigation.description'),
            spotlightPadding: 0,
         },
         {
            id: 'invoice-tabs',
            target: isMobile ? '[data-tour="mobile-invoice-tabs"]' : '[data-tour="invoice-tabs"]',
            placement: isMobile ? 'bottom' : 'bottom-start',
            title: translate('tutorial.invoiceTabs.title'),
            content: translate('tutorial.invoiceTabs.description'),
            spotlightPadding: isMobile ? 4 : { top: 16, right: 16, bottom: 12, left: 16 },
         },
      ];

      if (isAdmin) {
         tutorialSteps.push({
            id: 'action-buttons',
            target: '[data-tour="invoice-actions"]',
            placement: isMobile ? 'bottom' : 'bottom-end',
            title: translate('tutorial.actionButtons.title'),
            content: translate('tutorial.actionButtons.description'),
            spotlightPadding: 8,
         });
      }

      tutorialSteps.push({
         id: 'invoice-details',
         target: isMobile
            ? '[data-tour="invoice-details-mobile"]'
            : '[data-tour="invoice-details"]',
         placement: isMobile ? 'top' : 'bottom-start',
         title: translate('tutorial.invoiceDetails.title'),
         content: translate('tutorial.invoiceDetails.description'),
         spotlightPadding: isMobile ? 4 : 0,
      });

      return tutorialSteps;
   }, [isAdmin, isMobile, translate]);

   const completeTutorial = () => {
      if (!userKey) return;

      void updateInvoiceTutorialVersion({ version: INVOICE_TUTORIAL_VERSION });
   };

   const handleSkip = () => {
      completeTutorial();
      setStartAfterWelcome(false);
      setShowWelcome(false);
   };

   const handleStart = () => {
      setStartAfterWelcome(true);
      setShowWelcome(false);
   };

   const handleWelcomeExited = () => {
      if (!startAfterWelcome) return;

      setStartAfterWelcome(false);
      setRunTour(true);
   };

   const handleTourEvent = ({ status, type }: EventData) => {
      if (type !== EVENTS.TOUR_END) return;

      setRunTour(false);
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
         completeTutorial();
      }
   };

   return (
      <>
         <Modal
            show={showWelcome}
            onHide={handleSkip}
            onExited={handleWelcomeExited}
            backdrop='static'
            backdropClassName='accountitz-tutorial-backdrop'
            dialogClassName='accountitz-tutorial-welcome'
            contentClassName='accountitz-tutorial-welcome-content'
            centered
            keyboard={false}
         >
            <Modal.Body className='accountitz-tutorial-welcome-body'>
               <h1>{translate('tutorial.welcome.title')}</h1>
               <p>{translate('tutorial.welcome.description')}</p>
               <div className='accountitz-tutorial-welcome-actions'>
                  <button type='button' className='outline-primary' onClick={handleSkip}>
                     {translate('tutorial.skip')}
                  </button>
                  <button type='button' className='primary' onClick={handleStart}>
                     {translate('tutorial.start')}
                  </button>
               </div>
            </Modal.Body>
         </Modal>

         <Joyride
            continuous
            run={runTour}
            steps={steps}
            tooltipComponent={TutorialTooltip}
            onEvent={handleTourEvent}
            options={{
               backgroundColor: '#ffffff',
               blockTargetInteraction: true,
               buttons: ['primary'],
               dismissKeyAction: false,
               offset: 10,
               overlayClickAction: false,
               overlayColor: 'rgba(21, 37, 54, 0.25)',
               primaryColor: '#1e6264',
               scrollDuration: 300,
               scrollOffset: 20,
               skipBeacon: true,
               spotlightRadius: 8,
               targetWaitTimeout: 4000,
               textColor: '#191b23',
               width: 321,
               zIndex: 2000,
            }}
            floatingOptions={{ hideArrow: true }}
            locale={{
               last: translate('tutorial.done'),
               next: translate('tutorial.next'),
            }}
            styles={{
               floater: { filter: 'none' },
               tooltip: { backgroundColor: 'transparent', padding: 0 },
            }}
         />
      </>
   );
};

export default AccountitzTutorial;
